import React, { useState } from 'react';
import styles from './table-filter.module.scss';
import Button from '../button/Button';
import { FilterItem, FilterProps } from './table-filter.type';
import CommonDialog from '../../dialog/CommonDialog';
import { CaretDownIcon } from '../../../../assets/icons';
import { DateRangePicker, Range } from 'react-date-range';

const Filter: React.FC<FilterProps> = ({
  filterItems,
  onApplyFilter,
  onResetFilter,
  onClose,
  isOpen,
  className = '',
}) => {
  if (!isOpen) return null;

  return (
    <CommonDialog noPadding>
      <div className={styles.filterContainer}>
        <div className={styles.filterHeader}>
          <h2>Filter</h2>
          <button
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close"
          >
            <span className={styles.closeIcon}>&times;</span>
          </button>
        </div>

        <div className={styles.filterContent}>
          {filterItems.map((item) => (
            <FilterSection key={item.id} item={item} />
          ))}
        </div>

        <div className={styles.filterFooter}>
          {onResetFilter && (
            <Button
              text="Reset"
              variant="outline"
              onClick={onResetFilter}
              className={styles.resetButton}
            />
          )}
          <Button
            text="Apply Filter"
            variant="secondary"
            onClick={onApplyFilter}
            className={styles.applyButton}
          />
        </div>
      </div>
      </CommonDialog>
  );
};

const FilterSection: React.FC<{ item: FilterItem }> = ({ item }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className={styles.filterSection}>
      <div className={styles.sectionHeader} onClick={toggleExpand}>
        <h3>{item.label}</h3>
         <CaretDownIcon />
      </div>

      {isExpanded && (
        <div className={styles.sectionContent}>
          {item.type === 'checkbox' && (
            <CheckboxFilter 
              options={item.options} 
              onChange={item.onChange} 
            />
          )}
          
          {item.type === 'radio' && (
            <RadioFilter 
              options={item.options} 
              onChange={item.onChange} 
            />
          )}
          
          {item.type === 'date' && (
            <DateRangeFilter 
              initialDateRange={item.initialDateRange}
              onChange={item.onChange}
            />
          )}
          
          {item.type === 'dropdown' && (
            <DropdownFilter 
              options={item.options}
              selectedValue={item.selectedValue}
              onChange={item.onChange}
            />
          )}
          
          {item.type === 'search' && (
            <SearchFilter 
              placeholder={item.placeholder}
              value={item.value}
              onChange={item.onChange}
            />
          )}
        </div>
      )}
    </div>
  );
};

interface CheckboxFilterProps {
  options: {
    id: string;
    label: string;
    value: string;
    icon?: React.ReactNode;
    isSelected?: boolean;
  }[];
  onChange: (selectedValues: string[]) => void;
}

const CheckboxFilter: React.FC<CheckboxFilterProps> = ({ options, onChange }) => {
  const [selectedOptions, setSelectedOptions] = useState<string[]>(
    options.filter(opt => opt.isSelected).map(opt => opt.value)
  );

  const handleCheckboxChange = (value: string) => {
    const newSelected = selectedOptions.includes(value)
      ? selectedOptions.filter(val => val !== value)
      : [...selectedOptions, value];
    
    setSelectedOptions(newSelected);
    onChange(newSelected);
  };

  return (
    <div className={styles.checkboxFilter}>
      {options.map((option) => (
        <div key={option.id} className={styles.filterOption}>
          <label className={styles.checkboxLabel}>
            <div className={styles.optionContent}>
              {option.icon && <span className={styles.optionIcon}>{option.icon}</span>}
              <span className={styles.optionLabel}>{option.label}</span>
            </div>
            <input
              type="checkbox"
              checked={selectedOptions.includes(option.value)}
              onChange={() => handleCheckboxChange(option.value)}
              className={styles.checkbox}
            />
            <span className={styles.checkmark}></span>
          </label>
        </div>
      ))}
    </div>
  );
};

interface RadioFilterProps {
  options: {
    id: string;
    label: string;
    value: string;
    icon?: React.ReactNode;
    isSelected?: boolean;
  }[];
  onChange: (selectedValue: string) => void;
}

const RadioFilter: React.FC<RadioFilterProps> = ({ options, onChange }) => {
  const defaultSelected = options.find(opt => opt.isSelected)?.value || '';
  const [selectedOption, setSelectedOption] = useState<string>(defaultSelected);

  const handleRadioChange = (value: string) => {
    setSelectedOption(value);
    onChange(value);
  };

  return (
    <div className={styles.radioFilter}>
      {options.map((option) => (
        <div key={option.id} className={styles.filterOption}>
          <label className={styles.radioLabel}>
            <div className={styles.optionContent}>
              {option.icon && <span className={styles.optionIcon}>{option.icon}</span>}
              <span className={styles.optionLabel}>{option.label}</span>
            </div>
            <input
              type="radio"
              checked={selectedOption === option.value}
              onChange={() => handleRadioChange(option.value)}
              className={styles.radio}
              name={`radio-${option.id}`}
            />
            <span className={styles.radiomark}></span>
          </label>
        </div>
      ))}
    </div>
  );
};

interface DateRangeFilterProps {
  initialDateRange?: Range;
  onChange: (range: Range) => void;
}

const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  initialDateRange,
  onChange,
}) => {
  // Default date range if none provided
  const today = new Date();
  const todayStart = new Date(today.setHours(0, 0, 0, 0));
  const defaultRange: Range = {
    startDate: todayStart,
    endDate: today,
    key: "selection",
  };

  const dateRange = initialDateRange || defaultRange;

  return (
    <div className={styles.dateRangeWrapper}>
      <DateRangePicker
          ranges={[dateRange]}
          onChange={onChange}
          moveRangeOnFirstSelection={false}
          months={1}
          direction="horizontal"
          maxDate={today}
          showDateDisplay={false}
          rangeColors={['#007bff']}
        />
    </div>
  );
};

interface DropdownFilterProps {
  options: {
    id: string;
    label: string;
    value: string;
  }[];
  selectedValue?: string;
  onChange: (selectedValue: string) => void;
}

const DropdownFilter: React.FC<DropdownFilterProps> = ({ 
  options, 
  selectedValue, 
  onChange 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(selectedValue || '');

  const handleSelect = (value: string) => {
    setSelected(value);
    onChange(value);
    setIsOpen(false);
  };

  return (
    <div className={styles.dropdownFilter}>
      <div
        className={styles.dropdownSelected}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>
          {selected ? options.find(opt => opt.value === selected)?.label : 'Select an option'}
        </span>
        <span className={`${styles.dropdownIcon} ${isOpen ? styles.open : ''}`}>▼</span>
      </div>

      {isOpen && (
        <div className={styles.dropdownOptions}>
          {options.map((option) => (
            <div
              key={option.id}
              className={`${styles.dropdownOption} ${selected === option.value ? styles.selected : ''}`}
              onClick={() => handleSelect(option.value)}
            >
              {option.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

interface SearchFilterProps {
  placeholder?: string;
  value?: string;
  onChange: (value: string) => void;
}

const SearchFilter: React.FC<SearchFilterProps> = ({ 
  placeholder, 
  value = '', 
  onChange 
}) => {
  const [searchValue, setSearchValue] = useState(value);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    onChange(e.target.value);
  };

  return (
    <div className={styles.searchFilter}>
      <input
        type="text"
        placeholder={placeholder || 'Search...'}
        value={searchValue}
        onChange={handleChange}
        className={styles.searchInput}
      />
    </div>
  );
};

export default Filter;